import { useMemo } from 'react';
import { isWithinDateRange } from '~/utils/isWithinDateRange';
import { CampaignBannerType, CarCardType } from '~/data/types/car/cars';
import { IBannerPromotion } from '~/data/types/car/cars';
import { CAR_TARGET } from '~/data/consts';

interface UseCarCampaignsResult {
  campaign: IBannerPromotion | null;
  isPromoted: boolean;
  isInRangeDate: boolean;
  isTimerDisplayed: boolean;
  isCampaignBanner: boolean;
  shouldDisplay: boolean;
  isCarPageBanner: any;
  promotionIconUrl: string | { url: string };
}

const useCarCampaigns = (
  car: Pick<CarCardType, 'id'>,
  promotionCampaignCards: IBannerPromotion[] = [],
  page: string,
): UseCarCampaignsResult => {
  const carId = car?.id;

  const campaign = useMemo(() => {
    return (
      promotionCampaignCards.find(
        (campaign) =>
          campaign.carIds?.some(
            ({ carId: id, page: campaignPage }) =>
              id === carId && campaignPage === page,
          ),
      ) || null
    );
  }, [carId, promotionCampaignCards, page]);

  const isPromoted = !!campaign;

  const isInRangeDate = useMemo(() => {
    if (!campaign || !campaign.startDate || !campaign.endDate) {
      return false;
    }
    return isWithinDateRange(
      new Date(campaign.startDate),
      new Date(campaign.endDate),
    );
  }, [campaign]);

  const isTimerDisplayed = campaign?.showTimer || false;

  const isCampaignBanner =
    campaign?.campaignBannerType === CampaignBannerType.TIMER;

  const shouldDisplay = isCampaignBanner && isInRangeDate;

  const isCarPageBanner = useMemo(() => {
    return (
      campaign?.showCarPageBanner &&
      (campaign?.target === page || campaign?.target === CAR_TARGET.ALL)
    );
  }, [campaign?.showCarPageBanner, campaign?.target, page]);

  const promotionIconUrl = useMemo(() => {
    if (isPromoted && isInRangeDate && campaign && campaign.icon) {
      return campaign.icon || '';
    }
    return '';
  }, [isInRangeDate, isPromoted, campaign]);

  return {
    campaign,
    isPromoted,
    isInRangeDate,
    isTimerDisplayed,
    isCampaignBanner,
    shouldDisplay,
    isCarPageBanner,
    promotionIconUrl,
  };
};

export default useCarCampaigns;
